// auto-generated by Typewriter template _models.tst from PortCall.EndPoints\Api\ItineraryPoint\ApiItineraryPoint.cs
import S, { DataSignal } from 's-js';
import SArray, { SDataArray } from 's-array';
import TZDate from 'lib/TZDate';
import { jsonableObservable } from 'lib/jsonableObservable';
import { BerthReservation, BerthReservationData, BerthReservationOptions } from 'data/models/BerthReservation';
import { Change, ChangeData, ChangeOptions } from 'data/models/Change';
import { ItineraryPointSelf, ItineraryPointSelfData, ItineraryPointSelfOptions } from 'data/models/ItineraryPointSelf';
import { ItineraryPointVersion, ItineraryPointVersionData, ItineraryPointVersionOptions } from 'data/models/ItineraryPointVersion';
import { Location, LocationData, LocationOptions } from 'data/models/Location';
import { PilotJobOrder, PilotJobOrderData, PilotJobOrderOptions } from 'data/models/PilotJobOrder';
import { PortCall, PortCallData, PortCallOptions } from 'data/models/PortCall';
import { ScheduleDate, ScheduleDateData, ScheduleDateOptions } from 'data/models/ScheduleDate';
import { ScheduleDateEntity, ScheduleDateEntityData, ScheduleDateEntityOptions } from 'data/models/ScheduleDateEntity';

// ItineraryPointData is the class that ItineraryPoint serializes to/from
export interface ItineraryPointData {
    Id : number;
    Url : string | null;
	
    ArrivalPilotJobOrders : PilotJobOrderData[] | null;
    BerthReservation : BerthReservationData | null;
    Change : ChangeData | null;
    ChangeId : number;
    DeparturePilotJobOrders : PilotJobOrderData[] | null;
    Location : LocationData | null;
    LocationId : number;
    PortCall : PortCallData | null;
    PortCallId : number;
    Schedule : ScheduleDateEntityData[] | null;
    Self : ItineraryPointSelfData | null;
    Version : ItineraryPointVersionData | null;
	
    Arrival : ScheduleDateData;
    Departure : ScheduleDateData;
    Guid : /* GUID */ string;
    IsMovementPoint : boolean;
    IsPortArrival : boolean;
    IsPortDeparture : boolean;
}

// ItineraryPointOptions is the class which may be passed to the ItineraryPoint constructor to define optional initial data values
export interface ItineraryPointOptions {
    Id : number;
    Url? : string | null;
	
    ArrivalPilotJobOrders? : PilotJobOrderOptions[] | null;
    BerthReservation? : BerthReservationOptions | null;
    Change? : ChangeOptions | null;
    ChangeId? : number;
    DeparturePilotJobOrders? : PilotJobOrderOptions[] | null;
    Location? : LocationOptions | null;
    LocationId? : number;
    PortCall? : PortCallOptions | null;
    PortCallId? : number;
    Schedule? : ScheduleDateEntityOptions[] | null;
    Self? : ItineraryPointSelfOptions | null;
    Version? : ItineraryPointVersionOptions | null;
	
    Arrival? : ScheduleDateOptions;
    Departure? : ScheduleDateOptions;
    Guid? : /* GUID */ string;
    IsPortArrival? : boolean;
    IsPortDeparture? : boolean;
}

export class ItineraryPoint {
    public Id : DataSignal<number>;
    public Url : DataSignal<string | null>;
	
    public ArrivalPilotJobOrders : SDataArray<PilotJobOrder>;
    public BerthReservation : DataSignal<BerthReservation | null>;
    public Change : DataSignal<Change | null>;
    public ChangeId : DataSignal<number>;
    public DeparturePilotJobOrders : SDataArray<PilotJobOrder>;
    public Location : DataSignal<Location | null>;
    public LocationId : DataSignal<number>;
    public PortCall : DataSignal<PortCall | null>;
    public PortCallId : DataSignal<number>;
    public Schedule : SDataArray<ScheduleDateEntity>;
    public Self : DataSignal<ItineraryPointSelf | null>;
    public Version : DataSignal<ItineraryPointVersion | null>;
	
    public Arrival : ScheduleDate;
    public Departure : ScheduleDate;
    public Guid : DataSignal<string>;
    public IsPortArrival : DataSignal<boolean>;
    public IsPortDeparture : DataSignal<boolean>;

    constructor();
    constructor(data : ItineraryPointOptions);
    constructor(data: ItineraryPointOptions | null = null) {
        this.Id = jsonableObservable(S.value<number>(data && data.Id !== undefined ? data.Id : 0));
        this.Url = jsonableObservable(S.value<string | null>(data && data.Url !== undefined ? data.Url : null));
	    
        this.ArrivalPilotJobOrders = SArray<PilotJobOrder>([]);
        this.BerthReservation = S.value<BerthReservation | null>(null);
        this.Change = S.value<Change | null>(null);
        this.ChangeId = jsonableObservable(S.value<number>(data && data.ChangeId !== undefined ? data.ChangeId : 0));
        this.DeparturePilotJobOrders = SArray<PilotJobOrder>([]);
        this.Location = S.value<Location | null>(null);
        this.LocationId = jsonableObservable(S.value<number>(data && data.LocationId !== undefined ? data.LocationId : 0));
        this.PortCall = S.value<PortCall | null>(null);
        this.PortCallId = jsonableObservable(S.value<number>(data && data.PortCallId !== undefined ? data.PortCallId : 0));
        this.Schedule = SArray<ScheduleDateEntity>([]);
        this.Self = S.value<ItineraryPointSelf | null>(null);
        this.Version = S.value<ItineraryPointVersion | null>(null);
	    
        this.Arrival = data && data.Arrival !== undefined ? new ScheduleDate(data.Arrival) : new ScheduleDate();
        this.Departure = data && data.Departure !== undefined ? new ScheduleDate(data.Departure) : new ScheduleDate();
        this.Guid = jsonableObservable(S.value<string>(data && data.Guid !== undefined ? data.Guid : '00000000-0000-0000-0000-000000000000'));
        this.IsPortArrival = jsonableObservable(S.value<boolean>(data && data.IsPortArrival !== undefined ? data.IsPortArrival : false));
        this.IsPortDeparture = jsonableObservable(S.value<boolean>(data && data.IsPortDeparture !== undefined ? data.IsPortDeparture : false));
    }

    static key(obj : ItineraryPoint) {
        return obj.Id();
    }
    
    static dataKey(obj : ItineraryPointOptions) {
        return obj.Id;
    }

    static update(obj : ItineraryPoint, data : ItineraryPointOptions) {
        S.freeze(() => {
            if (data.Id !== undefined) obj.Id(data.Id);
            if (data.Url !== undefined) obj.Url(data.Url);
	        
            if (data.ChangeId !== undefined) obj.ChangeId(data.ChangeId);
            if (data.LocationId !== undefined) obj.LocationId(data.LocationId);
            if (data.PortCallId !== undefined) obj.PortCallId(data.PortCallId);
	        
            if (data.Arrival !== undefined) ScheduleDate.update(obj.Arrival, data.Arrival);
            if (data.Departure !== undefined) ScheduleDate.update(obj.Departure, data.Departure);
            if (data.Guid !== undefined) obj.Guid(data.Guid);
            if (data.IsPortArrival !== undefined) obj.IsPortArrival(data.IsPortArrival);
            if (data.IsPortDeparture !== undefined) obj.IsPortDeparture(data.IsPortDeparture);
        });
    }
}



// WEBPACK FOOTER //
// ./server/models/ItineraryPoint.ts